<template>
  <div class="exam-results-view">
    <div class="mb-6">
      <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
        Exam Results
      </h1>
      <p class="text-secondary-text dark:text-secondary-dark-text">
        Review an employee's most recent exam results.
      </p>
    </div>

    <!-- Tabs -->
    <div class="mb-6">
      <div class="border-b border-gray-200 dark:border-gray-700">
        <nav class="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            @click="activeTab = 'current'"
            :class="[
              activeTab === 'current'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
              'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200'
            ]"
          >
            <ClipboardList
              :class="[
                activeTab === 'current' ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500',
                '-ml-0.5 mr-2 h-5 w-5'
              ]"
            />
            <span>Current Results</span>
            <span
              v-if="filteredExamResults.length > 0"
              :class="[
                activeTab === 'current'
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
                'ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium'
              ]"
            >
              {{ filteredExamResults.length }}
            </span>
          </button>

          <!-- Attempt History tab hidden for now -->
          <!--
          <button
            @click="activeTab = 'history'"
            :class="[
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300',
              'group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200'
            ]"
          >
            <History
              :class="[
                activeTab === 'history' ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-500',
                '-ml-0.5 mr-2 h-5 w-5'
              ]"
            />
            <span>Attempt History</span>
            <span
              v-if="filteredAttemptHistory.length > 0"
              :class="[
                activeTab === 'history'
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
                'ml-3 py-0.5 px-2.5 rounded-full text-xs font-medium'
              ]"
            >
              {{ filteredAttemptHistory.length }}
            </span>
          </button>
          -->
        </nav>
      </div>
    </div>

    <!-- Date Range Filter Section (Separate Row) -->
    <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4 shadow-sm">
      <div class="flex flex-col lg:flex-row lg:items-end gap-4">
        <!-- Date Range Filter -->
        <div class="flex-1">
          <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            <Calendar class="w-4 h-4 inline-block mr-1" />
            Filter by Date Range
          </label>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <!-- Start Date -->
            <div>
              <label class="block text-xs text-secondary-text dark:text-secondary-dark-text mb-1">
                From Date
              </label>
              <input
                v-model="dateRange.start"
                type="date"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 text-sm"
                placeholder="Start date"
              />
            </div>
            
            <!-- End Date -->
            <div>
              <label class="block text-xs text-secondary-text dark:text-secondary-dark-text mb-1">
                To Date
              </label>
              <input
                v-model="dateRange.end"
                type="date"
                :min="dateRange.start"
                :disabled="!dateRange.start"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="End date"
              />
            </div>
          </div>
        </div>

        <!-- Quick Filter Presets -->
        <div class="flex-1">
          <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            Quick Filters
          </label>
          <div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 gap-2">
            <button
              @click="setDateRangePreset('today')"
              :class="[
                'px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200',
                isPresetActive('today')
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]"
            >
              Today
            </button>
            <button
              @click="setDateRangePreset('week')"
              :class="[
                'px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200',
                isPresetActive('week')
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]"
            >
              This Week
            </button>
            <button
              @click="setDateRangePreset('month')"
              :class="[
                'px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200',
                isPresetActive('month')
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]"
            >
              This Month
            </button>
            <button
              @click="setDateRangePreset('year')"
              :class="[
                'px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200',
                isPresetActive('year')
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              ]"
            >
              This Year
            </button>
          </div>
        </div>

        <!-- Clear Filter Button -->
        <div class="flex items-end">
          <BaseButton
            text="Clear"
            variant="danger"
            size="sm"
            :icon="X"
            :disabled="!dateRange.start && !dateRange.end"
            @click="clearDateFilter"
          />
        </div>
      </div>

      <!-- Active Filter Info -->
      <div v-if="dateRange.start || dateRange.end" class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
        <div class="flex items-center gap-2 text-sm">
          <span class="text-secondary-text dark:text-secondary-dark-text">Active Filter:</span>
          <span class="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md font-medium">
            {{ formatDateRange() }}
          </span>
          <span class="text-secondary-text dark:text-secondary-dark-text">
            ({{ filteredExamResults.length }} results)
          </span>
        </div>
      </div>
    </div>

    <!-- Current Results Tab Content -->
    <div v-if="activeTab === 'current'">
      <!-- Custom Grouped Current Results Table -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <!-- Header -->
        <div class="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div class="flex items-center gap-3">
              <ClipboardList class="w-5 h-5 text-blue-500" />
              <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
                Current Results
              </h2>
              <span class="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">
                {{ groupedExamResults.length }} group{{ groupedExamResults.length !== 1 ? 's' : '' }}
              </span>
            </div>
            <!-- Search -->
            <div class="w-full sm:w-64">
              <input
                v-model="currentSearchQuery"
                type="text"
                placeholder="Search by employee or exam..."
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 text-sm"
              />
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="p-8 text-center">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <p class="mt-2 text-sm text-secondary-text dark:text-secondary-dark-text">Loading exam results...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="groupedExamResults.length === 0" class="p-8 text-center">
          <ClipboardList class="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p class="text-sm text-secondary-text dark:text-secondary-dark-text">No exam results found.</p>
        </div>

        <!-- Content (Desktop & Mobile) -->
        <div v-else>
          <!-- Desktop Table View (hidden on mobile/tablet) -->
          <div class="hidden xl:block overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead class="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Employee
                  </th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Exam
                  </th>
                  <th scope="col" class="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Score
                  </th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date Taken
                  </th>
                  <th scope="col" class="w-16 px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                <tr
                  v-for="group in groupedExamResults"
                  :key="group.key"
                  class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ group.latestResult.employee?.user_details?.full_name || group.latestResult.employee?.email || 'N/A' }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      {{ group.latestResult.employee?.email }}
                    </div>
                  </td>
                  <td class="px-6 py-4">
                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ group.latestResult.exam?.exam_name || 'N/A' }}
                    </div>
                    <div v-if="group.latestResult.exam?.lesson" class="text-xs text-gray-500 dark:text-gray-400">
                      {{ group.latestResult.exam.lesson.name }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-center">
                    <div class="text-sm font-medium text-gray-900 dark:text-white">
                      {{ formatScore(group.latestResult.user_score) }}/{{ formatScore(group.latestResult.total_score) }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      {{ formatPercentage(group.latestResult.user_score, group.latestResult.total_score) }}%
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      :class="[
                        'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                        getStatusClass(group.latestResult.remarks)
                      ]"
                    >
                      {{ group.latestResult.remarks_label }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm text-gray-900 dark:text-white">
                      {{ group.latestResult.date_taken_formatted }}
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      @click.stop="viewExamResult(group.latestResult.exam_result_id)"
                      class="p-2 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title="View Details"
                    >
                      <Eye class="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile Card View (visible on mobile/tablet only) -->
          <div class="xl:hidden divide-y divide-gray-200 dark:divide-gray-700">
            <div
              v-for="group in groupedExamResults"
              :key="group.key"
              class="p-4"
            >
              <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                <div class="p-4">
                  <!-- Employee Info -->
                  <div class="mb-3">
                    <h3 class="text-base font-semibold text-gray-900 dark:text-white mb-1">
                      {{ group.latestResult.employee?.user_details?.full_name || group.latestResult.employee?.email || 'N/A' }}
                    </h3>
                    <p class="text-sm text-secondary-text dark:text-secondary-dark-text">
                      {{ group.latestResult.employee?.email }}
                    </p>
                  </div>

                  <!-- Exam Info -->
                  <div class="mb-3">
                    <div class="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      {{ group.latestResult.exam?.exam_name || 'N/A' }}
                    </div>
                    <div v-if="group.latestResult.exam?.lesson" class="text-xs text-secondary-text dark:text-secondary-dark-text">
                      Lesson: {{ group.latestResult.exam.lesson.name }}
                    </div>
                  </div>

                  <!-- Result Details -->
                  <div class="space-y-2 text-sm">
                    <div class="flex justify-between items-center">
                      <span class="text-secondary-text dark:text-secondary-dark-text">Score:</span>
                      <div class="text-right">
                        <span class="text-gray-900 dark:text-white font-medium">
                          {{ formatScore(group.latestResult.user_score) }}/{{ formatScore(group.latestResult.total_score) }}
                        </span>
                        <span class="text-xs text-secondary-text dark:text-secondary-dark-text ml-1">
                          ({{ formatPercentage(group.latestResult.user_score, group.latestResult.total_score) }}%)
                        </span>
                      </div>
                    </div>

                    <div class="flex justify-between items-center">
                      <span class="text-secondary-text dark:text-secondary-dark-text">Status:</span>
                      <span
                        :class="[
                          'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                          getStatusClass(group.latestResult.remarks)
                        ]"
                      >
                        {{ group.latestResult.remarks_label }}
                      </span>
                    </div>

                    <div class="flex justify-between">
                      <span class="text-secondary-text dark:text-secondary-dark-text">Date Taken:</span>
                      <span class="text-gray-900 dark:text-white font-medium">
                        {{ group.latestResult.date_taken_formatted }}
                      </span>
                    </div>
                  </div>

                  <!-- Action Button -->
                  <div class="flex justify-end pt-3 mt-3 border-t border-gray-200 dark:border-gray-700">
                    <button
                      @click="viewExamResult(group.latestResult.exam_result_id)"
                      class="p-2 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title="View Details"
                    >
                      <Eye class="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination (if needed) -->
        <div v-if="groupedExamResults.length > 0" class="px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <div class="text-sm text-secondary-text dark:text-secondary-dark-text">
              Showing {{ groupedExamResults.length }} exam result{{ groupedExamResults.length !== 1 ? 's' : '' }}
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useTitle } from '@/composables/ui/useTitle'
import { examResultService } from '@/services/examResultService'
import type { ExamResult, ExamAttemptHistory } from '@/services/examResultService'
import BaseButton from '@/components/common/BaseButton.vue'
import { ClipboardList, Eye, Calendar, X, History, Archive } from 'lucide-vue-next'
import { useRouter } from 'vue-router'

// Page title
const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Douglas Private Care Services')
useTitle(`Exam Results - ${appTitle.value}`)

const router = useRouter()
const loading = ref(false)
const examResults = ref<ExamResult[]>([])
const currentSearchQuery = ref('')

// Load active tab from localStorage
const loadActiveTabFromStorage = (): 'current' | 'history' => {
  try {
    const savedTab = localStorage.getItem('examResultsActiveTab')
    if (savedTab === 'current' || savedTab === 'history') {
      return savedTab
    }
  } catch (error) {
    console.error('Failed to load active tab from localStorage:', error)
  }
  return 'current'
}

// Save active tab to localStorage
const saveActiveTabToStorage = (tab: 'current' | 'history') => {
  try {
    localStorage.setItem('examResultsActiveTab', tab)
  } catch (error) {
    console.error('Failed to save active tab to localStorage:', error)
  }
}

// Tab state - initialize from localStorage
const activeTab = ref<'current' | 'history'>(loadActiveTabFromStorage())

// Watch for active tab changes and save to localStorage
watch(activeTab, (newValue) => {
  saveActiveTabToStorage(newValue)
})

// Attempt History state
const historyLoading = ref(false)
const attemptHistory = ref<ExamAttemptHistory[]>([])
const expandedGroups = ref<Set<string>>(new Set())
const historySearchQuery = ref('')

// Load date range from localStorage
const loadDateRangeFromStorage = () => {
  try {
    const savedDateRange = localStorage.getItem('examResultsDateRange')
    if (savedDateRange) {
      return JSON.parse(savedDateRange)
    }
  } catch (error) {
    console.error('Failed to load date range from localStorage:', error)
  }
  return { start: '', end: '' }
}

// Save date range to localStorage
const saveDateRangeToStorage = (range: { start: string; end: string }) => {
  try {
    localStorage.setItem('examResultsDateRange', JSON.stringify(range))
  } catch (error) {
    console.error('Failed to save date range to localStorage:', error)
  }
}

// Date range filter state - initialize from localStorage
const dateRange = ref(loadDateRangeFromStorage())

// Load active preset from localStorage
const loadActivePresetFromStorage = (): string | null => {
  try {
    const savedPreset = localStorage.getItem('examResultsActivePreset')
    return savedPreset || null
  } catch (error) {
    console.error('Failed to load active preset from localStorage:', error)
    return null
  }
}

// Save active preset to localStorage
const saveActivePresetToStorage = (preset: string | null) => {
  try {
    if (preset) {
      localStorage.setItem('examResultsActivePreset', preset)
    } else {
      localStorage.removeItem('examResultsActivePreset')
    }
  } catch (error) {
    console.error('Failed to save active preset to localStorage:', error)
  }
}

const activePreset = ref<string | null>(loadActivePresetFromStorage())

// Watch for active preset changes and save to localStorage
watch(activePreset, (newValue) => {
  saveActivePresetToStorage(newValue)
})

// Watch for date range changes and save to localStorage
watch(
  dateRange,
  (newValue) => {
    saveDateRangeToStorage(newValue)
    
    // If "To Date" is before "From Date", clear "To Date"
    if (newValue.start && newValue.end) {
      const startDate = new Date(newValue.start)
      const endDate = new Date(newValue.end)
      if (endDate < startDate) {
        dateRange.value.end = ''
      }
    }
  },
  { deep: true }
)

// Filtered and formatted exam results
const filteredExamResults = computed(() => {
  let filtered = examResults.value

  // Apply date range filter
  if (dateRange.value.start || dateRange.value.end) {
    filtered = filtered.filter(item => {
      if (!item.date_taken) return false
      
      const itemDate = new Date(item.date_taken)
      itemDate.setHours(0, 0, 0, 0)
      
      if (dateRange.value.start) {
        const startDate = new Date(dateRange.value.start)
        startDate.setHours(0, 0, 0, 0)
        if (itemDate < startDate) return false
      }
      
      if (dateRange.value.end) {
        const endDate = new Date(dateRange.value.end)
        endDate.setHours(23, 59, 59, 999)
        if (itemDate > endDate) return false
      }
      
      return true
    })
  }

  // Apply search filter
  if (currentSearchQuery.value.trim()) {
    const query = currentSearchQuery.value.toLowerCase().trim()
    filtered = filtered.filter(item => {
      const employeeName = (item.employee?.user_details?.full_name || '').toLowerCase()
      const employeeEmail = (item.employee?.email || '').toLowerCase()
      const examName = (item.exam?.exam_name || '').toLowerCase()
      const lessonName = (item.exam?.lesson?.name || '').toLowerCase()
      
      return employeeName.includes(query) || 
             employeeEmail.includes(query) || 
             examName.includes(query) || 
             lessonName.includes(query)
    })
  }

  // Map to display format
  return filtered.map(item => ({
    ...item,
    employee_name: item.employee?.user_details?.full_name || item.employee?.email || '',
    exam_name: item.exam?.exam_name || '',
    lesson_name: item.exam?.lesson?.name || '',
    remarks_label: item.remarks_label || '', 
    exam_result_id: item.exam_result_id
  }))
})

// Group current exam results by employee + exam
interface GroupedExamResult {
  key: string
  employeeId: number
  examId: number
  latestResult: ExamResult
  results: ExamResult[]
}

const groupedExamResults = computed<GroupedExamResult[]>(() => {
  const groups = new Map<string, GroupedExamResult>()
  
  filteredExamResults.value.forEach(result => {
    // Create a unique key for grouping by employee + exam
    const employeeId = result.employee?.user_id || 0
    const examId = result.exam?.exam_id || 0
    const key = `${employeeId}_${examId}`
    
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        employeeId,
        examId,
        latestResult: result,
        results: []
      })
    }
    
    const group = groups.get(key)
    if (group) {
      group.results.push(result)
      
      // Update latest result if this one is more recent
      if (new Date(result.date_taken || 0) > new Date(group.latestResult.date_taken || 0)) {
        group.latestResult = result
      }
    }
  })
  
  // Sort groups by latest date (newest first) and sort results within each group
  return Array.from(groups.values())
    .sort((a, b) => {
      const dateA = new Date(a.latestResult.date_taken || 0).getTime()
      const dateB = new Date(b.latestResult.date_taken || 0).getTime()
      return dateB - dateA
    })
    .map(group => ({
      ...group,
      results: group.results.sort((a, b) => {
        // Sort by date taken (newest first)
        const dateA = new Date(a.date_taken || 0).getTime()
        const dateB = new Date(b.date_taken || 0).getTime()
        return dateB - dateA
      })
    }))
})

// Toggle current group expansion (removed — no longer needed)
// const toggleCurrentGroup = (key: string) => {
//   if (expandedCurrentGroups.value.has(key)) {
//     expandedCurrentGroups.value.delete(key)
//   } else {
//     expandedCurrentGroups.value.add(key)
//   }
// }

// Filtered attempt history
const filteredAttemptHistory = computed(() => {
  let filtered = attemptHistory.value

  // Apply date range filter (using reset_date for history)
  if (dateRange.value.start || dateRange.value.end) {
    filtered = filtered.filter(item => {
      if (!item.reset_date) return false
      
      const itemDate = new Date(item.reset_date)
      itemDate.setHours(0, 0, 0, 0)
      
      if (dateRange.value.start) {
        const startDate = new Date(dateRange.value.start)
        startDate.setHours(0, 0, 0, 0)
        if (itemDate < startDate) return false
      }
      
      if (dateRange.value.end) {
        const endDate = new Date(dateRange.value.end)
        endDate.setHours(23, 59, 59, 999)
        if (itemDate > endDate) return false
      }
      
      return true
    })
  }

  // Apply search filter
  if (historySearchQuery.value.trim()) {
    const query = historySearchQuery.value.toLowerCase().trim()
    filtered = filtered.filter(item => {
      const employeeName = getEmployeeName(item).toLowerCase()
      const employeeEmail = getEmployeeEmail(item).toLowerCase()
      const examName = (item.exam?.exam_name || '').toLowerCase()
      const lessonName = (item.exam?.lesson?.name || '').toLowerCase()
      
      return employeeName.includes(query) || 
             employeeEmail.includes(query) || 
             examName.includes(query) || 
             lessonName.includes(query)
    })
  }

  return filtered
})

// Group attempt history by Restart Date, employee, and exam
interface GroupedAttempt {
  key: string
  resetDate: string
  resetDateFormatted: string
  resetBy: any
  attempts: ExamAttemptHistory[]
}

const groupedAttemptHistory = computed<GroupedAttempt[]>(() => {
  const groups = new Map<string, GroupedAttempt>()
  
  filteredAttemptHistory.value.forEach(attempt => {
    // Create a unique key for grouping by reset_date + employee + exam
    const employeeId = attempt.employee?.user_id || 'unknown'
    const examId = attempt.exam?.exam_id || 'unknown'
    const resetDate = attempt.reset_date || 'unknown'
    const key = `${resetDate}_${employeeId}_${examId}`
    
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        resetDate: attempt.reset_date || '',
        resetDateFormatted: attempt.reset_date_formatted || '',
        resetBy: attempt.reset_by,
        attempts: []
      })
    }
    
    const group = groups.get(key)
    if (group) {
      group.attempts.push(attempt)
    }
  })
  
  // Sort groups by Restart Date (newest first) and sort attempts within each group
  return Array.from(groups.values())
    .sort((a, b) => {
      const dateA = new Date(a.resetDate).getTime()
      const dateB = new Date(b.resetDate).getTime()
      return dateB - dateA
    })
    .map(group => ({
      ...group,
      attempts: group.attempts.sort((a, b) => {
        // Sort by attempt number
        return (a.num_of_attempts || 0) - (b.num_of_attempts || 0)
      })
    }))
})

// Toggle group expansion
const toggleGroup = (key: string) => {
  if (expandedGroups.value.has(key)) {
    expandedGroups.value.delete(key)
  } else {
    expandedGroups.value.add(key)
  }
}

// Helper functions for attempt history
const getEmployeeName = (item: ExamAttemptHistory): string => {
  return item.employee?.user_details?.full_name || item.employee?.email || 'N/A'
}

const getEmployeeEmail = (item: ExamAttemptHistory): string => {
  return item.employee?.email || ''
}

// Date range preset functions
const setDateRangePreset = (preset: string) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  activePreset.value = preset
  
  switch (preset) {
    case 'today':
      dateRange.value.start = formatDateForInput(today)
      dateRange.value.end = formatDateForInput(today)
      break
      
    case 'week': {
      const startOfWeek = new Date(today)
      const day = startOfWeek.getDay()
      const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1) // Adjust when day is Sunday
      startOfWeek.setDate(diff)
      
      const endOfWeek = new Date(startOfWeek)
      endOfWeek.setDate(startOfWeek.getDate() + 6)
      
      dateRange.value.start = formatDateForInput(startOfWeek)
      dateRange.value.end = formatDateForInput(endOfWeek)
      break
    }
      
    case 'month': {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      
      dateRange.value.start = formatDateForInput(startOfMonth)
      dateRange.value.end = formatDateForInput(endOfMonth)
      break
    }
      
    case 'year': {
      const startOfYear = new Date(today.getFullYear(), 0, 1)
      const endOfYear = new Date(today.getFullYear(), 11, 31)
      
      dateRange.value.start = formatDateForInput(startOfYear)
      dateRange.value.end = formatDateForInput(endOfYear)
      break
    }
  }
}

const isPresetActive = (preset: string) => {
  return activePreset.value === preset
}

const clearDateFilter = () => {
  dateRange.value.start = ''
  dateRange.value.end = ''
  activePreset.value = null
}

const formatDateForInput = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatDateRange = () => {
  if (dateRange.value.start && dateRange.value.end) {
    const start = new Date(dateRange.value.start).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    })
    const end = new Date(dateRange.value.end).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    })
    return `${start} - ${end}`
  } else if (dateRange.value.start) {
    return `From ${new Date(dateRange.value.start).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    })}`
  } else if (dateRange.value.end) {
    return `Until ${new Date(dateRange.value.end).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    })}`
  }
  return ''
}

// View exam result details
const viewExamResult = (examResultId: number) => {
  console.log('🔍 Viewing exam result with ID:', examResultId)
  console.log('📋 Available exam results IDs:', examResults.value.map(r => r.exam_result_id))
  
  // Verify the ID exists in our data
  const resultExists = examResults.value.some(r => r.exam_result_id === examResultId)
  console.log('✅ Result exists in current data:', resultExists)
  
  if (!resultExists) {
    console.error('❌ Exam result ID not found in current data:', examResultId)
    return
  }
  
  router.push({ 
    name: 'ResultsExamView', 
    params: { id: examResultId.toString() } 
  })
}

// Helper for status color
function getStatusClass(remarks: number) {
  if (remarks === 1) return 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
  if (remarks === 0) return 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
  return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
}

onMounted(async () => {
  loading.value = true
  historyLoading.value = true
  
  try {
    console.log('📥 Loading exam results...')
    const response = await examResultService.getAllExamResults({ 
      sort_by: 'date_taken', 
      sort_order: 'desc' 
    })
    
    // Handle both paginated and non-paginated responses
    if (Array.isArray(response.data)) {
      examResults.value = response.data
    } else {
      examResults.value = response.data.data || []
    }
    
    console.log('✅ Loaded exam results:', examResults.value.length)
    console.log('📊 Exam results data:', examResults.value)
    
  } catch (err) {
    console.error('❌ Failed to load exam results:', err)
    examResults.value = []
  } finally {
    loading.value = false
  }

  // Load attempt history
  try {
    console.log('📥 Loading attempt history...')
    
    // Note: For now, we'll fetch all attempt history
    // You may want to implement per-employee filtering or pagination
    const historyResponse = await examResultService.getAllAttemptHistory({
      sort_by: 'reset_date',
      sort_order: 'desc'
    })
    
    attemptHistory.value = historyResponse.data || []
    
    console.log('✅ Loaded attempt history:', attemptHistory.value.length)
    console.log('📊 Attempt history data:', attemptHistory.value)
    
  } catch (err) {
    console.error('❌ Failed to load attempt history:', err)
    attemptHistory.value = []
  } finally {
    historyLoading.value = false
  }
})

// Ensure scores are displayed as whole numbers and calculate safe percentages
const formatScore = (score?: number | null) => {
  const n = Number(score ?? 0)
  if (Number.isNaN(n)) return 0
  return Math.round(n)
}

const calculatePercentage = (user?: number | null, total?: number | null) => {
  const u = Number(user ?? 0)
  const t = Number(total ?? 0)
  if (Number.isNaN(u) || Number.isNaN(t) || t <= 0) return 0
  const pct = Math.round((u / t) * 100)
  return Math.min(100, Math.max(0, pct))
}

const formatPercentage = (user?: number | null, total?: number | null) => {
  return calculatePercentage(user, total)
}
</script>

<style scoped>
.exam-results-view {
  animation: slideInUp 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1476px) {
  /* Force the DataTable desktop table to hide and show the mobile cards */
  :deep(.hidden.xl\:block) {
    display: none !important;
  }

  :deep(.xl\:hidden) {
    display: block !important;
  }

  /* Truncate the second column to keep rows tidy on smaller screens */
  :deep(.min-w-full th:nth-child(2)),
  :deep(.min-w-full td:nth-child(2)) {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>